import { type NodeId, type NodeSn, getRecordKeys } from '@ipfabric/utils';
import {
  type DeviceViewEntities,
  type NodeDetailArgs,
  isNodeEntity,
} from 'components/DeviceView/deviceViewTypes';
import { findDeviceViewEntityByNodeSn } from 'components/DeviceView/helpers/findDeviceViewEntityByNodeSn';
import { initPathLookupPathInspector } from 'components/Graph/commands/pathLookup/initPathLookupPathInspector';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { changePathLookupPathInspectorActiveNode } from './changePathLookupPathInspectorActiveNode';
import { destroyPathLookupPathInspectorNode } from './destroyPathLookupPathInspectorNode';

export interface RefreshPathInspectorNodeIdsCommand {
  activeNodeId: NodeId | null;
  activeNodeSn: NodeSn | null;
  deviceViewEntities: DeviceViewEntities;
  updatedNodeData: Map<NodeSn, NodeDetailArgs>;
}

export const refreshPathInspectorNodeIds: GraphCommandFactory<
  RefreshPathInspectorNodeIdsCommand
> = (cmdContext, dispatch) => (command) => {
  const pathInspectorNodeSns: NodeSn[] = getRecordKeys(
    cmdContext.getState().pathlookup.pathInspector.selectedPackets,
  );
  const activeNodeSnChanged =
    cmdContext.getState().pathlookup.pathInspector.activeNodeSn !==
    command.activeNodeSn;

  /**
   * Delete path inspector nodes which don't exist anymore
   */
  for (const nodeSn of pathInspectorNodeSns) {
    if (!command.updatedNodeData.has(nodeSn)) {
      dispatch(destroyPathLookupPathInspectorNode, { nodeSn });
    }
  }

  /**
   * Delete remaining, valid path inspector nodes which will be reinitialized later.
   * Set new active node if current node does not exist in new graph data.
   */
  cmdContext.updateState((draft) => {
    for (const nodeSn of pathInspectorNodeSns) {
      if (command.updatedNodeData.has(nodeSn)) {
        delete draft.pathlookup.pathInspector.enlargedEdges[nodeSn];
        delete draft.pathlookup.pathInspector.highlightedEdges[nodeSn];
        delete draft.pathlookup.pathInspector.selectedPackets[nodeSn];
      }
    }
    if (activeNodeSnChanged) {
      draft.pathlookup.pathInspector.activeNodeSn = command.activeNodeSn;
    }
  });

  // Reinitialize valid nodes
  command.updatedNodeData.forEach((nodeData, nodeSn) => {
    if (!nodeData?.isPathLookupNode) {
      return;
    }
    const deviceViewEntity = command.deviceViewEntities.find(
      (entity) => isNodeEntity(entity) && entity.sn === nodeSn,
    );
    if (nodeData !== undefined && deviceViewEntity !== undefined) {
      dispatch(initPathLookupPathInspector, { nodeSn });
    }
  });

  // If active node has been reset, update graph to show correct highlighted edges
  const currentActiveNodeSn =
    cmdContext.getState().pathlookup.pathInspector.activeNodeSn;
  const deviceViewEntity = findDeviceViewEntityByNodeSn(
    cmdContext.getDeviceView().deviceViewEntities,
    command.activeNodeSn,
  );
  if (
    activeNodeSnChanged &&
    currentActiveNodeSn !== null &&
    deviceViewEntity !== null
  ) {
    dispatch(changePathLookupPathInspectorActiveNode, {
      deviceViewElement: 'node',
      deviceViewEntityId: deviceViewEntity.id,
    });
  }
};
